import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { fetchFooterSocialLinks } from '../../redux/actions/FooterSocialLinksAction';
import { fetchAboutUs } from '../../redux/actions/aboutUsAction';
import { fetchContactInfo } from '../../redux/actions/contactAction';

function Footer() {
  const { t, i18n } = useTranslation('global');
  const dispatch = useDispatch();

  // Redux state
  const socialLinks = useSelector((s) => s.footerSocialLinks.data); // [{ platform, url }]
  const about = useSelector((s) => s.aboutUs.data); // Object (localized via Accept-Language)
  const aboutLoading = useSelector((s) => s.aboutUs.isLoading);
  const aboutError = useSelector((s) => s.aboutUs.error);
  const contact = useSelector((s) => s.contact.data); // { phones[], email, whatsapp_link }

  // Fetch
  useEffect(() => { dispatch(fetchFooterSocialLinks()); }, [dispatch]);
  useEffect(() => {
    dispatch(fetchAboutUs(i18n.language));
    dispatch(fetchContactInfo(i18n.language));
  }, [dispatch, i18n.language]);

  // Helpers
  const textAlignStyle = useMemo(
    () => ({ textAlign: i18n.language === 'ar' ? 'right' : 'left' }),
    [i18n.language]
  );

  const platformIcon = {
    facebook: 'fab fa-facebook',
    instagram: 'fab fa-instagram',
    twitter: 'fab fa-x-twitter',
    linkedin: 'fab fa-linkedin',
  };

  const socials = (socialLinks || [])
    .map((s) => ({ ...s, key: s.platform?.toLowerCase() }))
    .filter((s) => s.key && ['facebook', 'instagram', 'twitter', 'linkedin'].includes(s.key));

  const phones = Array.isArray(contact?.phones)
    ? contact.phones
    : contact?.phones
    ? [contact.phones]
    : [];

  const getLocalized = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return val[i18n.language] || val.ar || val.en || Object.values(val)[0] || '';
    }
    return '';
  };

  return (
    <footer
      dir={i18n.dir()}
      className="footer-root text-white py-5"
      style={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0b0b0b',
      }}
    >
      {/* Inline styles scoped to this component, including overlay + dark mode */}
      <style>{`
        .footer-root { position: relative; }
        .footer-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,.58); /* overlay for readability in light */
          pointer-events: none;
        }
        /* Ensure content is above overlay */
        .footer-root > .container, .footer-root hr, .footer-root .logo { position: relative; z-index: 1; }
        .footer-root hr { border-color: rgba(255,255,255,.15) !important; }

        /* Social hover */
        .footer-root .footer-social-link { opacity: 0.9; transition: opacity .2s ease, transform .2s ease; }
        .footer-root .footer-social-link:hover { opacity: 1; transform: translateY(-2px); }

        /* Contact minis */
        .footer-root .footer-contact-simple li { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        .footer-root .footer-contact-simple i { width: 18px; text-align: center; font-size: 16px; opacity: .9; }
        .footer-root .contact-values { display: flex; flex-wrap: wrap; gap: 6px 10px; }
        .footer-root .contact-link { color: #fff; text-decoration: none; border-bottom: 1px dotted rgba(255,255,255,0.35); }
        .footer-root .contact-link:hover { opacity: 1; border-bottom-color: transparent; }
        .footer-root .contact-whatsapp-icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 50%; background: #22c55e; color: #0b1d13; text-decoration: none; border: 1px solid rgba(0,0,0,.12); }
        .footer-root .contact-whatsapp-icon i { font-size: 20px; }
        .footer-root .contact-whatsapp-icon:hover { filter: brightness(1.05); }

        /* If your app toggles a .dark class on html/body */
        .dark .footer-root { background-image: none !important; background-color: #000 !important; }
        .dark .footer-root::before { background: rgba(0,0,0,.9) !important; }

        /* OS-level dark mode fallback */
        @media (prefers-color-scheme: dark) {
          .footer-root { background-image: none; background-color: #000; }
          .footer-root::before { background: rgba(0,0,0,.9); }
        }
      `}</style>

      <div className="container">
        <div className="row text-center text-md-start">
          {/* About */}
          <div className="col-md-3 mb-4" style={textAlignStyle}>
            {aboutLoading ? (
              <div style={{ color: 'orange' }}>{t('common.loading')}</div>
            ) : aboutError ? (
              <div style={{ color: 'red' }}>{t('common.error')}</div>
            ) : about ? (
              <div style={{ marginBottom: 10 }}>
                <img
                  className="mx-auto mb-3"
                  src={about.logoUrl || '/ToolsWorldLogo.jfif'}
                  alt={t('footer.altLogo')}
                  style={{ width: 50, borderRadius: 10 }}
                />
                <div style={{ fontSize: 14, margin: '6px 0', lineHeight: 2 }}>
                  {getLocalized(about.description) || t('footer.aboutFallback')}
                </div>
              </div>
            ) : null}
          </div>

          {/* Quick Links */}
            <div className="col-md-3 mb-4" style={textAlignStyle}>
            <h6 className="text-warning mb-2">{t('footer.quickLinks.title')}</h6>
            <ul className="list-unstyled">
                <li className="mb-2">
                <Link
                    to="/contact"
                    className="text-white text-decoration-none d-inline-flex align-items-center gap-2"
                    aria-label={t('footer.contactUs')}
                >
                    <i className="fa-regular fa-paper-plane" aria-hidden="true"></i>
                    <span>{t('footer.contactUs')}</span>
                </Link>
                </li>
            </ul>
            </div>

            {/* Useful Links */}
            <div className="col-md-3 mb-4" style={textAlignStyle}>
            <h6 className="text-warning mb-2">{t('footer.usefulLinks.title')}</h6>
            <ul className="list-unstyled">
                <li className="mb-2">
                <Link
                    to="/contact"
                    className="text-white text-decoration-none d-inline-flex align-items-center gap-2"
                    aria-label={t('footer.contactUs')}
                >
                    <i className="fa-regular fa-paper-plane" aria-hidden="true"></i>
                    <span>{t('footer.contactUs')}</span>
                </Link>
                </li>
                <li className="mb-2">
                <Link
                    to="/terms"
                    className="text-white text-decoration-none d-inline-flex align-items-center gap-2"
                    aria-label={t('footer.terms')}
                >
                    <i className="fa-solid fa-file-contract" aria-hidden="true"></i>
                    <span>{t('footer.terms', 'Terms & Conditions')}</span>
                </Link>
                </li>
                <li className="mb-2">
                <Link
                    to="/privacy"
                    className="text-white text-decoration-none d-inline-flex align-items-center gap-2"
                    aria-label={t('footer.privacy')}
                >
                    <i className="fa-solid fa-user-shield" aria-hidden="true"></i>
                    <span>{t('footer.privacy', 'Privacy Policy')}</span>
                </Link>
                </li>
            </ul>
            </div>


          {/* Stay in Touch — phone (stacked) + email + WhatsApp icon */}
          <div className="col-md-3 mb-4" style={textAlignStyle}>
            <h6 className="text-warning mb-3">{t('footer.stayInTouch.title')}</h6>

            <ul className="list-unstyled small footer-contact-simple">
              {phones.map((p, i) => (
                <li key={i}>
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <a href={`tel:${p}`} className="contact-link">{p}</a>
                </li>
              ))}

              {contact?.email && (
                <li>
                  <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                  <a href={`mailto:${contact.email}`} className="contact-link">{contact.email}</a>
                </li>
              )}
            </ul>

            {contact?.whatsapp_link && (
              <a
                href={contact.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="contact-whatsapp-icon"
              >
                <i className="fa-brands fa-whatsapp" />
              </a>
            )}
          </div>
        </div>

        <hr className="border-secondary" />

        {/* Bottom bar */}
        <div className="d-flex justify-content-between flex-column flex-md-row align-items-center px-3 pb-3">
          <a
            href="https://brmja.tech/"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-0 d-block text-white text-decoration-none"
          >
            Copyright 2025 ©{' '}
            <span style={{ color: 'orange', cursor: 'pointer' }}>{t('footer.copyright')}</span>
          </a>

          <div className="d-flex align-items-center gap-3">
            <span className="fw-bold">{t('footer.follow')}</span>
            {socials.map((s, idx) => {
              const icon = platformIcon[s.key] || (s.key === 'twitter' ? 'fab fa-twitter' : '');
              return (
                <a
                  key={idx}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.platform}
                  className="footer-social-link"
                >
                  <i className={`${icon} text-white fs-5`} />
                </a>
              );
            })}
          </div>
        </div>

        <div className="logo my-1">
          <img
            src="/brmjatech.webp"
            className="img-dark-mode"
            alt="brmja tech"
            style={{ width: 180, margin: '0 auto', display: 'block' }}
          />
        </div>
      </div>
    </footer>
  );
}

export default Footer;
